import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { OrganizationCards } from '@components/OrganizationCards';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useQueries } from '@tanstack/react-query';
import { useInvalidateQueriesOnChange } from 'app/hooks/useInvalidateQueriesOnChange';
import { createFetchOrgWorkspacesQueryFn } from 'app/queries/organizations/createFetchOrgWorkspacesQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { UserDetails } from './components/UserDetails';
import { WorkspacesCards } from './components/WorkspacesCards';

import { Content } from './styles/Content';

export const AccountPage = () => {
  const [workspacesDisclosureOpen, setWorkspacesDisclosureOpen] = useState<boolean>(true);
  const [organizationsDisclosureOpen, setOrganizationsDisclosureOpen] = useState<boolean>(true);

  const { me, notificationsReceiveDate } = useAuthContext();
  const { call } = useAPI();
  const organizations = me.organizations ?? [];

  const workspacesQueries = useQueries({
    queries: organizations.map((organization) => ({
      queryKey: organizationsKeys.workspacesByOrg(organization.id),
      queryFn: createFetchOrgWorkspacesQueryFn(call),
    })),
  });
  useInvalidateQueriesOnChange(me.organizations, organizationsKeys.workspaces());
  useInvalidateQueriesOnChange(notificationsReceiveDate, organizationsKeys.workspaces());

  const allWorkspaces = workspacesQueries.flatMap((query) => query.data ?? []);
  const isWorkspacesLoading = workspacesQueries.some((query) => query.isLoading);
  const failedWorkspacesQueries = workspacesQueries.filter((query) => query.isError);

  const retryWorkspaces = () => {
    failedWorkspacesQueries.forEach((query) => void query.refetch());
  };

  const workspaceSectionTitle = `My Workspaces${allWorkspaces.length > 0 ? ` (${allWorkspaces.length})` : ''}`;
  const organizationSectionTitle = `My Organizations${organizations.length > 0 ? ` (${organizations.length})` : ''}`;

  return (
    <>
      <Helmet>
        <title>My Account - AutoScript</title>
      </Helmet>
      <Header title="My Account" />

      <Content>
        <div>
          <UserDetails />
        </div>
        <div>
          <DisclosureSection
            title={organizationSectionTitle}
            open={organizationsDisclosureOpen}
            setOpenState={setOrganizationsDisclosureOpen}
            loading={false}
          >
            <OrganizationCards />
          </DisclosureSection>
        </div>
        <div>
          <DisclosureSection
            title={workspaceSectionTitle}
            open={workspacesDisclosureOpen}
            setOpenState={setWorkspacesDisclosureOpen}
            loading={isWorkspacesLoading}
          >
            {failedWorkspacesQueries.length > 0 ? (
              <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
                <span>Failed to load workspaces.</span>
                <TextButton type="button" onClick={retryWorkspaces}>
                  Retry
                </TextButton>
              </div>
            ) : null}
            <WorkspacesCards workspaces={allWorkspaces} />
          </DisclosureSection>
        </div>
      </Content>
    </>
  );
};
